/**
 * The complete §11 operation registry — the single vocabulary the service
 * layer routes, the authorizer permissions against, and the MCP membrane
 * (B22) fronts. Registration is closed: a duplicate name throws at module
 * load, and nothing outside this file adds operations.
 */

import type { OperationContract } from "./common.js";
import * as audit from "./audit.js";
import * as audience from "./audience.js";
import * as billing from "./billing.js";
import * as capability from "./capability.js";
import * as catalog from "./catalog.js";
import * as channels from "./channels.js";
import * as groups from "./groups.js";
import * as clientErrors from "./client-errors.js";
import * as commerce from "./commerce.js";
import * as connect from "./connect.js";
import * as devices from "./devices.js";
import * as features from "./features.js";
import * as redemption from "./redemption.js";
import * as sessions from "./sessions.js";
import * as staff from "./staff.js";
import * as funnel from "./funnel.js";
import * as analytics from "./analytics.js";
import * as platform from "./platform.js";
import * as ota from "./ota.js";
import * as apiKeys from "./api-keys.js";
import * as webhooks from "./webhooks.js";
import * as octo from "./octo.js";
import * as viator from "./viator.js";
import * as supply from "./supply.js";
import * as setupDraft from "./setup-draft.js";
import * as selfService from "./self-service.js";
import * as memberships from "./memberships.js";
import * as customers from "./customers.js";
import * as pricingConfig from "./pricing-config.js";

function collect(...modules: Record<string, unknown>[]): Readonly<Record<string, OperationContract>> {
  const out: Record<string, OperationContract> = {};
  for (const mod of modules) {
    for (const value of Object.values(mod)) {
      const op = value as OperationContract;
      if (typeof op !== "object" || op === null || typeof op.name !== "string") continue;
      if (op.name in out) throw new Error(`Duplicate operation contract: ${op.name}`);
      out[op.name] = op;
    }
  }
  return Object.freeze(out);
}

export const operations = collect(catalog, channels, groups, clientErrors, supply, commerce, connect, redemption, sessions, staff, devices, capability, audit, billing, funnel, analytics, features, platform, ota, apiKeys, webhooks, octo, viator, audience, setupDraft, selfService, pricingConfig, customers, memberships);

/**
 * The operations that are REACHABLE — every §11 op but the internal one.
 *
 * This derivation had one home and two readers were about to grow (B509):
 * `infra/test/infra.test.ts` asserts the deployed TEMPLATE carries a
 * `POST /ops/<name>` route for each of these, and `ops-deployed-probe.mjs`
 * asserts the same of the STAGE. A second hand-written list is the defect
 * that probe exists to end, so there is one function and both read it.
 */
export function externalOperations(): readonly OperationContract[] {
  return Object.freeze(Object.values(operations).filter((o) => o.kind !== "internal"));
}

/** The same set, as the names a route key or a URL path is built from. */
export function externalOperationNames(): readonly string[] {
  return Object.freeze(externalOperations().map((o) => o.name).sort());
}

